
import { isTokenExpired, isTokenExpiringSoon } from '../utils/tokenUtils';

const BASE = (process.env.REACT_APP_API_BASE || "http://localhost:4000").replace(/\/$/, '');
console.log("[api] BASE =", BASE);   // <- will print on page load

function authHeaders() {
  const token = localStorage.getItem("token");
  console.log("🔑 Auth token:", token ? `${token.substring(0, 20)}...` : "No token found");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Check if token needs refresh and refresh if necessary
 * Returns true if token is valid or was successfully refreshed
 */
async function ensureValidToken() {
  const token = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");
  
  console.log("🔍 ensureValidToken check:", {
    hasToken: !!token,
    hasRefreshToken: !!refreshToken
  });
  
  // No tokens at all
  if (!token || !refreshToken) {
    console.log("❌ Missing tokens");
    return false;
  }
  
  // Check if token is expired or expiring soon (within 5 minutes)
  if (isTokenExpired(token)) {
    console.log("🔄 Token expired, attempting refresh...");
    try {
      const newToken = await refreshAccessToken(refreshToken);
      if (newToken) {
        localStorage.setItem("token", newToken);
        console.log("✅ Token refreshed successfully before API call");
        return true;
      } else {
        console.error("❌ Token refresh failed - no new token returned");
        return false;
      }
    } catch (error) {
      console.error("❌ Token refresh error:", error);
      return false;
    }
  } else if (isTokenExpiringSoon(token, 300)) { // 5 minutes
    console.log("⚠️ Token expiring soon, proactively refreshing...");
    try {
      const newToken = await refreshAccessToken(refreshToken);
      if (newToken) {
        localStorage.setItem("token", newToken);
        console.log("✅ Token proactively refreshed");
        return true;
      }
    } catch (error) {
      console.warn("⚠️ Proactive refresh failed, but token still valid:", error);
      return true; // Token is still valid even if refresh failed
    }
  }
  
  console.log("✅ Token is valid, no refresh needed");
  return true; // Token is valid
}

// --- Auth ---
export async function register(email, password, rememberMe = false) {
  const res = await fetch(`${BASE}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, rememberMe }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.ok === false) {
    throw new Error(body?.error || "register_failed");
  }
  return body; // { ok:true, data:{ token, refreshToken, user } }
}

// services/api.js
export async function login(email, password, rememberMe = false) {
  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, rememberMe }),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.ok === false) {
    const msg = body?.error || "login_failed";
    throw new Error(msg);
  }
  return body; // { ok:true, data:{ token, refreshToken, user } }
}

/** Exchange Firebase ID token (Google/Microsoft sign-in) for app JWT. */
export async function loginWithIdToken(idToken, rememberMe = false) {
  const res = await fetch(`${BASE}/auth/oauth`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken, rememberMe }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.ok === false) {
    throw new Error(body?.error || "OAuth sign-in failed");
  }
  return body;
}

// Refresh access token using refresh token
export async function refreshAccessToken(refreshToken) {
  try {
    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    const body = await res.json().catch(() => ({}));
    if (!res.ok || body?.ok === false) {
      throw new Error(body?.error || "refresh_failed");
    }
    return body.data?.token || null;
  } catch (error) {
    console.error("Failed to refresh token:", error);
    return null;
  }
}


// --- Sites (CRUD, all authed) ---
export async function getSites() {
  await ensureValidToken(); // Ensure token is valid before request
  const res = await fetch(`${BASE}/sites`, { headers: { ...authHeaders() } });
  if (!res.ok) throw new Error("getSites failed");
  return res.json();
}

export async function addSite(url, alertEmails = [], options = {}) {
  await ensureValidToken(); // Ensure token is valid before request
  const { 
    sslEnabled = true, 
    sslWarningDays = 30,
    redirectEnabled = true,
    expectedDestination = null
  } = options;
  
  const res = await fetch(`${BASE}/sites`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ 
      url, 
      alert_emails: alertEmails,
      ssl_check_enabled: sslEnabled,
      ssl_warning_days: sslWarningDays,
      redirect_enabled: redirectEnabled,
      expected_destination: expectedDestination
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.ok === false) {
    // surface friendly backend error, like free plan limit reached
    const msg = body?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body;
}

export async function deleteSite(id) {
  const res = await fetch(`${BASE}/sites/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });
  return res.json();
}

export async function checkNow(site_id) {
  const res = await fetch(`${BASE}/check-now`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ site_id }),
  });
  if (!res.ok) throw new Error("checkNow failed");
  return res.json();
}

// --- Logs per site ---
export async function getLogs(id, limit = 50) {
  const cleanId = String(id).replace(/^\/+/, ""); // strip any leading slash
  const res = await fetch(`${BASE}/sites/${cleanId}/logs?limit=${limit}`, {
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.ok === false) throw new Error("getLogs failed");
  return body;
}



export async function testAlert(url, status) {
  const res = await fetch(`${BASE}/test-alert`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ url, status }),
  });
  if (!res.ok) throw new Error("testAlert failed");
  return res.json();
}

export function logout() {
  localStorage.removeItem("token");
}

export async function forgotPassword(email) {
  const res = await fetch(`${BASE}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return res.json();
}

export async function resetPassword(token, newPassword) {
  const res = await fetch(`${BASE}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, newPassword }),
  });
  return res.json();
}

// ensure your api wrapper adds Authorization from localStorage
// Core fetch wrapper
export async function api(path, opts = {}) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE}${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
    credentials: 'include', // Include credentials for CORS
    ...(opts.body ? { body: opts.body } : {}),
  });

  // Throw on non-2xx so callers can catch
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return await res.json().catch(() => ({}));
}

// --- Profile ---
export async function getProfile() {
  await ensureValidToken(); // Ensure token is valid before request
  const res = await fetch(`${BASE}/auth/me`, { headers: authHeaders() });
  return res.json();
}

export async function updateProfile(data) {
  await ensureValidToken(); // Ensure token is valid before request
  console.log("🔗 API call - updateProfile:", {
    url: `${BASE}/auth/update`,
    method: "PUT",
    data: data,
    headers: authHeaders()
  });
  
  const res = await fetch(`${BASE}/auth/update`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(data),
  });
  
  const result = await res.json();
  console.log("📡 API response - updateProfile:", {
    status: res.status,
    ok: res.ok,
    result: result
  });
  
  return result;
}

export async function testNotification(type = "email") {
  const res = await fetch(`${BASE}/test-notification`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ type }),
  });
  return res.json();
}

// Settings API functions
export async function getSettings() {
  await ensureValidToken(); // Ensure token is valid before request
  const res = await fetch(`${BASE}/settings`, { headers: authHeaders() });
  if (!res.ok) throw new Error("getSettings failed");
  return res.json();
}

export async function updateSettings(settingsData) {
  await ensureValidToken(); // Ensure token is valid before request
  const res = await fetch(`${BASE}/settings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(settingsData),
  });
  if (!res.ok) throw new Error("updateSettings failed");
  return res.json();
}

export async function exportUserData() {
  await ensureValidToken(); // Ensure token is valid before request
  const res = await fetch(`${BASE}/settings/export`, { headers: authHeaders() });
  if (!res.ok) throw new Error("exportUserData failed");
  return res.json();
}
